import { $getFiltersRequestType, $Pagination } from '@shared/types';
import { z } from 'zod';
import { $CandidatePhoto } from './CandidatePhoto';

const $CandidateProcessFile = $CandidatePhoto.extend({
  isActive: z.boolean().optional(),
});

const $CandidateProcessFilter = z.object({
  resumeId: z.number(),
});

export const $CandidateProcessRequestParams = $getFiltersRequestType(
  $CandidateProcessFilter
);

export const $CandidateProcess = z.object({
  id: z.number(),
  processType: z.enum(['to_whats_app', 'from_whats_app']),
  contentType: z.enum(['text', 'audio', 'image', 'template']),
  status: z.string(),
  content: z.string(),
  sender: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      avatar: $CandidatePhoto.nullable().optional(),
    })
    .nullable(),
  transcription: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  file: z.array($CandidateProcessFile),
});

type CandidateProcess = z.infer<typeof $CandidateProcess>;

const getProcessId = (item: unknown) =>
  typeof item === 'object' && item !== null && 'id' in item
    ? item.id
    : undefined;

// Each message is validated separately so that one malformed message from the
// backend does not hide the whole conversation. Only the message id and the
// failed field paths are logged: message values are candidate data.
const toValidCandidateProcesses = (items: unknown[]) =>
  items.reduce<CandidateProcess[]>((processes, item) => {
    const result = $CandidateProcess.safeParse(item);

    if (result.success) {
      processes.push(result.data);
    } else {
      console.error('Skipped invalid candidate process', {
        id: getProcessId(item),
        issues: result.error.issues.map(({ path, message }) => ({
          path: path.join('.'),
          message,
        })),
      });
    }

    return processes;
  }, []);

export const $CandidateProcessListResponse = z.object({
  data: z.array(z.unknown()).transform(toValidCandidateProcesses),
  pagination: $Pagination,
});
